import { useEnum } from "@/api/services/global/enum";
import { FormField } from "@/components/elements/FormField";
import { InnerBox } from "@/components/elements/InnerBox";
import { Select } from "@/components/elements/Select";
import { CollapsibleSectionHeader, useCollapsible } from "@/features/talent/components/talent-common/CollapsibleSectionHeader";
import { TTalentForm } from "@/features/talent/types/talentForm";
import { divider } from "@/theme/colors";
import { FCC } from "@/types/common";
import { Grid, Collapse } from "@mui/material";
import { useEffect, useMemo } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { useGetUserListPersonInCharge } from "@/api/services/main/user";
import { useMe } from "@/api/services/main/auth";
import { useGetMatchInfo } from "@/api/services/main/match";
import { ERecruitmentStatus } from "@/types/enum";
import { useGetUserSettingInfo } from "@/api/services/main/user-setting";
import { truncateWithEllipsis } from "@/utils/string";

type TalentFormRecruitmentStatusProps = {
  personInChargeId?: number | null;
  matchOfficeId?: number | null;
};

export const TalentFormRecruitmentStatus: FCC<TalentFormRecruitmentStatusProps> = ({ personInChargeId, matchOfficeId }) => {
  const methods = useFormContext<TTalentForm>();
  const { control, setValue } = methods;
  const watchPersonInChargeId = useWatch({ control, name: "personInChargeId" });
  const { userOptions } = useGetUserListPersonInCharge(personInChargeId ?? watchPersonInChargeId);
  const { data: meResult } = useMe();
  const { data: recruitmentStatus } = useEnum("ERecruitmentStatus");
  const recruitmentStatusOptions = useMemo(() => recruitmentStatus ?? [], [recruitmentStatus]);

  const watchRecruitmentStatus = useWatch({ control, name: "recruitmentStatus" });
  const isDraft = watchRecruitmentStatus === ERecruitmentStatus.DRAFT;

  // Fetch match offices for 紹介事業所名 dropdown
  const { data: matchInfoData } = useGetMatchInfo();
  const matchOfficeOptions = useMemo(() => {
    const offices = matchInfoData?.data?.matchOffices ?? [];
    return offices
      .filter((office): office is { id: number; officeName: string } => office.id != null && !!office.officeName)
      .map((office) => ({
        value: office.id,
        label: truncateWithEllipsis(office.officeName, 13),
      }));
  }, [matchInfoData]);

  // Fetch user setting for auto-selecting 紹介事業所名 default
  const { data: userSettingData } = useGetUserSettingInfo();

  useEffect(() => {
    if (personInChargeId) return;

    const userData = meResult?.data;
    if (userData?.id && userData?.isMatchUser) {
      const parsedUserId = Number(userData?.id);
      if (!isNaN(parsedUserId)) {
        setValue("personInChargeId", parsedUserId);
      }
    }
  }, [setValue, meResult, personInChargeId]);

  // Auto-select matchOfficeId
  useEffect(() => {
    if (matchOfficeId !== undefined) return;

    const settingMatchOfficeId = userSettingData?.data?.matchOfficeId;
    if (settingMatchOfficeId && matchOfficeOptions.some((opt) => opt.value === settingMatchOfficeId)) {
      setValue("matchOfficeId", settingMatchOfficeId);
    }
  }, [setValue, userSettingData, matchOfficeId, matchOfficeOptions]);

  const { expanded, toggle } = useCollapsible();

  return (
    <InnerBox p={3} showDivider={false} borderColor={divider.high} bordered={true}>
      <CollapsibleSectionHeader title="ステータス状況" expanded={expanded} onToggle={toggle} />
      <Collapse in={expanded}>
        <Grid mt={1} container spacing={2}>
          <Grid item xs={4}>
            <FormField control={control} name="recruitmentStatus">
              <Select placeholder="選択してください" options={recruitmentStatusOptions} />
            </FormField>
          </Grid>
          <Grid item xs={4}>
            <FormField control={control} name="matchOfficeId" required={!isDraft}>
              <Select placeholder="選択してください" options={matchOfficeOptions} />
            </FormField>
          </Grid>
          <Grid item xs={4}>
            <FormField control={control} name="personInChargeId">
              <Select options={userOptions} />
            </FormField>
          </Grid>
        </Grid>
      </Collapse>
    </InnerBox>
  );
};
